// Główna scena: świat izometryczny, postać z predykcją, inni gracze i zombie z interpolacją, sterowanie dotykiem.
import Phaser from 'phaser';

import { api } from '../net/api.js';
import { C, E_PLAYER, E_ZOMBIE, STEP_S, type Delta, type EntityState, type GameEvent, type ServerMessage, type Welcome } from '../net/protocol.js';
import { GameSocket } from '../net/socket.js';
import { haptic, onAppState, onBackButton, onNetwork } from '../platform/native.js';
import { ChunkRenderer } from '../render/chunkRenderer.js';
import { textureFor } from '../render/sprites.js';
import { Interpolator } from '../state/interpolation.js';
import { Predictor } from '../state/prediction.js';
import { xpTotal } from '../state/progression.js';
import { closePanel, openInventory, openQuests } from '../ui/panels.js';
import { toast } from '../ui/dom.js';
import { Hud } from '../ui/hud.js';
import { Joystick } from '../ui/joystick.js';
import type { CharacterSummary } from '../ui/screens.js';
import { ChunkStore } from '../world/chunkStore.js';
import { Grid, type WorldConfig } from '../world/coords.js';
import { screenDirToWorld } from '../world/physics.js';

interface Ent {
  id: number; type: number; code: string; hp: number;
  view: Phaser.GameObjects.Container; bar: Phaser.GameObjects.Rectangle; interp: Interpolator;
  x: number; y: number;
}

export interface GameStart {
  char: CharacterSummary & { experience?: number; stats?: { hp: number; hp_max: number; hunger: number; thirst: number } };
  config: WorldConfig;
}

export class GameScene extends Phaser.Scene {
  private grid!: Grid;
  private chunks!: ChunkStore;
  private renderer2!: ChunkRenderer;
  private socket!: GameSocket;
  private predictor!: Predictor;
  private me!: Phaser.GameObjects.Container;
  private meX = 0;
  private meY = 0;
  private ents = new Map<number, Ent>();
  private hud!: Hud;
  private stick!: Joystick;
  private start!: GameStart;
  private targetId: number | null = null;
  private lastAttack = 0;
  private hp = 150;
  private hpMax = 150;
  private level = 1;
  private xp = 0;
  private alive = true;
  private welcomed = false;

  constructor() { super('game'); }

  init(data: GameStart): void { this.start = data; }

  create(): void {
    const { char, config } = this.start;
    this.grid = new Grid(config.tile_size_m, config.chunk_tiles);
    this.chunks = new ChunkStore(this.grid);
    this.renderer2 = new ChunkRenderer(this, this.grid);
    this.chunks.onLoaded = (c) => this.renderer2.add(c);
    this.chunks.onEvicted = (cx, cy) => this.renderer2.remove(cx, cy);
    const x0 = char.x ?? 0, y0 = char.y ?? 0;
    this.predictor = new Predictor(x0, y0, this.grid.tile, this.chunks.blocked);
    this.meX = x0; this.meY = y0;
    this.level = char.level; this.xp = char.experience ?? xpTotal(char.level);
    if (char.stats) { this.hp = char.stats.hp; this.hpMax = char.stats.hp_max; }

    this.me = this.makeCharacter('player_self', char.name, true);
    this.cameras.main.startFollow(this.me, true, 0.18, 0.18);
    this.cameras.main.setBackgroundColor('#050606');
    this.cameras.main.setZoom(Math.min(2, Math.max(1, Math.min(window.innerWidth, window.innerHeight) / 420)));

    this.hud = new Hud();
    this.stick = new Joystick();
    this.refreshHud(char.stats?.hunger ?? 100, char.stats?.thirst ?? 100);
    this.hud.onAction = (a) => this.action(a);
    this.hud.onChat = (msg) => this.socket.send({ t: C.CHAT, ch: 'l', msg });

    this.socket = new GameSocket(char.id);
    this.socket.onMessage = (m) => this.onServer(m);
    this.socket.onStatus = (s) => { this.hud.setNet(s); if (s === 'connecting') this.welcomed = false; };
    void this.socket.connect();

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => this.pickTarget(p.worldX, p.worldY));
    this.input.on('wheel', (_p: unknown, _o: unknown, _dx: number, dy: number) => {
      const cam = this.cameras.main;
      cam.setZoom(Phaser.Math.Clamp(cam.zoom * (dy > 0 ? 0.9 : 1.1), 0.6, 3));
    });
    this.input.keyboard?.on('keydown-SPACE', () => this.action('attack'));
    this.input.keyboard?.on('keydown-I', () => this.action('inventory'));
    this.input.keyboard?.on('keydown-ENTER', () => this.hud.toggleChat(true));

    this.time.addEvent({ delay: STEP_S * 1000, loop: true, callback: () => this.inputStep() });
    this.time.addEvent({ delay: 400, loop: true, callback: () => this.chunks.update(this.predictor.x, this.predictor.y) });
    this.time.addEvent({ delay: 500, loop: true, callback: () => this.drawMinimap() });
    this.time.addEvent({ delay: 3000, loop: true, callback: () => this.updateStreet() });
    this.time.addEvent({ delay: 1000, loop: true, callback: () => this.hud.setNet(this.socket.online ? 'online' : 'offline', this.socket.rtt) });
    this.chunks.update(x0, y0);
    this.updateStreet();

    onAppState((active) => { if (active) void this.socket.connect(); else this.socket.close(); });
    onNetwork((online) => { if (online && !this.socket.online) void this.socket.connect(); });
    onBackButton(() => closePanel());
    this.scale.on('resize', () => this.cameras.main.setZoom(Math.min(2, Math.max(1, Math.min(window.innerWidth, window.innerHeight) / 420))));
  }

  // ---------- sterowanie ----------
  private inputStep(): void {
    if (!this.welcomed || !this.alive) return;
    const [sx, sy] = this.stick.direction();
    if (!sx && !sy) return;
    const [dx, dy] = screenDirToWorld(sx, sy);
    const s = this.predictor.apply(dx, dy);
    this.socket.send({ t: C.MOVE, s: s.seq, dx, dy });
  }

  private action(a: string): void {
    void haptic('light');
    switch (a) {
      case 'attack': {
        if (this.targetId === null || !this.ents.has(this.targetId)) this.targetId = this.nearestZombie(25);
        this.attackTarget();
        break;
      }
      case 'heal': toast('Szybkie leczenie – Etap 4 (pasek umiejętności)', 'info'); break;
      case 'inventory': void openInventory(this.start.char.id); break;
      case 'quests': void openQuests(this.start.char.id); break;
      case 'chat': this.hud.toggleChat(); break;
      case 'menu': closePanel(); break;
    }
  }

  private attackTarget(): void {
    const now = performance.now();
    if (this.targetId === null || now - this.lastAttack < 250) return;
    this.lastAttack = now;
    this.socket.send({ t: C.ATTACK, s: this.predictor.seq, target: this.targetId });
  }

  private nearestZombie(maxDist: number): number | null {
    let best: number | null = null, bd = maxDist;
    for (const e of this.ents.values()) {
      if (e.type !== E_ZOMBIE) continue;
      const d = Math.hypot(e.x - this.predictor.x, e.y - this.predictor.y);
      if (d < bd) { bd = d; best = e.id; }
    }
    return best;
  }

  private pickTarget(sx: number, sy: number): void {
    const [wx, wy] = this.grid.screenToWorld(sx, sy + 18);   // +18 px: klikamy w tułów, nie w stopy
    let best: Ent | null = null, bd = 2.2;
    for (const e of this.ents.values()) {
      if (e.type !== E_ZOMBIE) continue;
      const d = Math.hypot(e.x - wx, e.y - wy);
      if (d < bd) { bd = d; best = e; }
    }
    if (best) { this.targetId = best.id; this.attackTarget(); void haptic('light'); }
  }

  // ---------- serwer ----------
  private onServer(m: ServerMessage): void {
    switch (m.t) {
      case 'wl': {
        const w = m as Welcome;
        this.predictor.teleport(w.x, w.y);
        this.meX = w.x; this.meY = w.y;
        this.hp = w.hp; this.hpMax = w.hp_max;
        this.welcomed = true;
        this.refreshHud();
        this.hud.chat('', 'Połączono z serwerem. Uważaj na siebie.', true);
        break;
      }
      case 'ak': this.predictor.reconcile(m.s, m.x, m.y); break;
      case 'dt': this.onDelta(m as Delta); break;
      case 'ev': this.onEvent(m as GameEvent); break;
      case 'cm': this.hud.chat(m.from, m.msg); break;
      case 'er': toast(m.msg, 'bad'); break;
    }
  }

  private onDelta(d: Delta): void {
    const now = performance.now();
    for (const st of d.ent) {
      const [id, type, x, y, hpPct, code] = st as EntityState;
      if (id === this.start.char.id) continue;
      let e = this.ents.get(id);
      if (!e) {
        const view = this.makeCharacter(type === E_PLAYER ? 'player' : code, type === E_PLAYER ? code : '', false);
        const bar = view.getAt(view.length - 1) as Phaser.GameObjects.Rectangle;
        e = { id, type, code, hp: hpPct, view, bar, interp: new Interpolator(120), x, y };
        this.ents.set(id, e);
      }
      e.interp.push(now, x, y);
      e.x = x; e.y = y; e.hp = hpPct;
      e.bar.width = 26 * hpPct / 100;
      if (id === this.targetId) this.hud.setTarget(type === E_ZOMBIE ? this.zombieName(code) : code, hpPct);
    }
    for (const id of d.gone) this.removeEnt(id);
  }

  private onEvent(ev: GameEvent): void {
    switch (ev.kind) {
      case 'hit': {
        const e = this.ents.get(ev.dst as number);
        if (e) this.floatText(e.x, e.y, ev.miss ? 'pudło' : String(ev.dmg), ev.crit ? '#f0c040' : '#e8e2d6');
        break;
      }
      case 'die': {
        const e = this.ents.get(ev.id as number);
        if (e) this.tweens.add({ targets: e.view, alpha: 0, duration: 400, onComplete: () => this.removeEnt(e.id) });
        if (ev.id === this.targetId) { this.targetId = null; this.hud.setTarget(null); }
        break;
      }
      case 'hurt': {
        this.hp = ev.hp as number;
        this.refreshHud();
        this.cameras.main.flash(120, 120, 20, 20);
        void haptic('medium');
        break;
      }
      case 'dead': {
        this.alive = false;
        void haptic('heavy');
        this.hud.showDeath(String(ev.by), () => { this.alive = true; });
        break;
      }
      case 'respawn': {
        this.predictor.teleport(ev.x as number, ev.y as number);
        this.hp = Math.floor(this.hpMax / 2);
        this.refreshHud();
        if (ev.at) toast(`Odrodzenie: ${ev.at}`, 'info');
        break;
      }
      case 'reward': {
        this.xp += ev.xp as number;
        if (ev.levelled) { this.level = ev.level as number; toast(`AWANS! Poziom ${this.level}`, 'good', 4000); void haptic('heavy'); }
        for (const l of (ev.loot as { item: string; qty: number }[]) ?? []) toast(`+${l.qty} ${l.item}`, 'good');
        if (ev.scrap) toast(`+${ev.scrap} Złomu`, 'good');
        for (const q of (ev.quests as { quest: string; completed?: boolean; title?: string }[]) ?? []) {
          if (q.completed) toast(`Zadanie ukończone!`, 'good', 4000);
          else if (q.title) toast(`Nowy etap: ${q.title}`, 'info', 4000);
        }
        this.refreshHud();
        break;
      }
    }
  }

  // ---------- render ----------
  update(_time: number, delta: number): void {
    const k = 1 - Math.exp(-delta / 60);
    this.meX += (this.predictor.x - this.meX) * k;
    this.meY += (this.predictor.y - this.meY) * k;
    this.place(this.me, this.meX, this.meY);
    const now = performance.now();
    for (const e of this.ents.values()) {
      const p = e.interp.sample(now);
      if (p) this.place(e.view, p[0], p[1]);
    }
    if (this.targetId !== null) {
      const t = this.ents.get(this.targetId);
      if (!t) { this.targetId = null; this.hud.setTarget(null); }
      else if (now - this.lastAttack > 450 && Math.hypot(t.x - this.predictor.x, t.y - this.predictor.y) < 30) this.attackTarget();
    }
  }

  private place(obj: Phaser.GameObjects.Container, x: number, y: number): void {
    const [sx, sy] = this.grid.worldToScreen(x, y);
    obj.setPosition(sx, sy);
    obj.setDepth(this.grid.depth(x, y, 5));
  }

  private makeCharacter(code: string, label: string, self: boolean): Phaser.GameObjects.Container {
    const body = this.add.image(0, 0, textureFor(this, code)).setOrigin(0.5, 0.92);
    const parts: Phaser.GameObjects.GameObject[] = [body];
    if (label) {
      parts.push(this.add.text(0, -body.height - 4, label, {
        fontFamily: 'system-ui, sans-serif', fontSize: '11px', color: self ? '#f0c040' : '#e8e2d6',
        stroke: '#000', strokeThickness: 3, resolution: 2,
      }).setOrigin(0.5, 1));
    }
    parts.push(this.add.rectangle(-13, -body.height - 2, 26, 3, 0xc2412d).setOrigin(0, 0.5));
    return this.add.container(0, 0, parts);
  }

  private removeEnt(id: number): void {
    const e = this.ents.get(id);
    if (!e) return;
    e.view.destroy();
    this.ents.delete(id);
  }

  private floatText(x: number, y: number, text: string, color: string): void {
    const [sx, sy] = this.grid.worldToScreen(x, y);
    const t = this.add.text(sx, sy - 48, text, { fontFamily: 'system-ui', fontSize: '14px', color, stroke: '#000', strokeThickness: 3 })
      .setOrigin(0.5).setDepth(2e9);
    this.tweens.add({ targets: t, y: sy - 80, alpha: 0, duration: 800, onComplete: () => t.destroy() });
  }

  private zombieName(code: string): string {
    return { zombie_walker: 'Zombie', zombie_runner: 'Zombie szybki', zombie_worker: 'Zombie robotnik',
      zombie_armored: 'Zombie opancerzony', zombie_screamer: 'Krzykaczka' }[code] ?? code;
  }

  private refreshHud(hunger?: number, thirst?: number): void {
    this.hud.hp.set(this.hp, this.hpMax);
    const lo = xpTotal(this.level), hi = xpTotal(this.level + 1);
    this.hud.xp.set(this.xp - lo, hi - lo, `${Math.floor(((this.xp - lo) / (hi - lo)) * 100)}%`);
    this.hud.setLevel(this.level);
    if (hunger !== undefined) this.hud.hunger.set(hunger, 100, 'Głód');
    if (thirst !== undefined) this.hud.thirst.set(thirst, 100, 'Woda');
  }

  private drawMinimap(): void {
    const chunks = [];
    const [pcx, pcy] = this.grid.worldToChunk(this.predictor.x, this.predictor.y);
    for (let j = -2; j <= 2; j++) for (let i = -2; i <= 2; i++) {
      const c = this.chunks.get(pcx + i, pcy + j);
      if (c) chunks.push(c);
    }
    const others: [number, number, string][] = [...this.ents.values()].map((e) => [e.x, e.y, e.type === E_ZOMBIE ? 'z' : 'p']);
    this.hud.drawMinimap(chunks, this.grid, this.predictor.x, this.predictor.y, others);
  }

  private async updateStreet(): Promise<void> {
    try {
      const r = await api<{ street: string | null; area: string | null }>(
        `/world/street/?x=${this.predictor.x.toFixed(1)}&y=${this.predictor.y.toFixed(1)}`, { auth: false });
      this.hud.setStreet(r.street, r.area);
    } catch { /* bez nazwy ulicy */ }
  }
}
